import { normalizeJsonFieldModelValue } from '@/utils/jsonFieldHelpers';

const structuredTypes = new Set(['json', 'geojson']);
const selectionTypes = new Set(['select', 'radio', 'radiogroup', 'tree-select']);
const multipleSelectionTypes = new Set(['multiselect', 'tags']);
const enabled = (value) => [true, 1, '1', 'true'].includes(value);
const isObject = (value) => value !== null && typeof value === 'object'
    && [Object.prototype, null].includes(Object.getPrototypeOf(value));

// Paths come from metadata, never from a leaf-name search through arbitrary JSON.
const fieldPaths = (sheet) => {
    const entries = [];
    const visit = (paragraphs, prefix = []) => {
        for (const paragraph of paragraphs ?? []) {
            if (!paragraph || typeof paragraph !== 'object') continue;
            const relation = String(paragraph.relation || (enabled(paragraph.repeatable) ? paragraph.repeatable_table : '') || '').split('.').filter(Boolean);
            const path = [...prefix, ...relation, ...(enabled(paragraph.repeatable) ? ['*'] : [])];
            for (const field of paragraph.form_fields ?? []) {
                if (!field?.name) continue;
                entries.push({ path: [...path, ...field.name.split('.')], field });
                if (enabled(field.repeatable) && field.repeatable_support_table && field.relation && field.relation !== field.name) {
                    entries.push({ path: [...path, ...field.relation.split('.')], field });
                }
            }
            visit(paragraph.form_paragraphs, path);
        }
    };
    visit(sheet?.form_paragraphs);
    return entries;
};

const matchingField = (entries, path) => entries.find((entry) => entry.path.length === path.length
    && entry.path.every((part, index) => part === path[index] || (part === '*' && /^\d+$/.test(path[index]))))?.field;

// Non-repeatable Vueform groups do not create data objects. A repeater below
// such groups still uses its own relation name at the root of the form payload.
const rootRepeatablePaths = (sheet) => {
    const paths = [];
    const visit = (paragraphs, prefix = []) => {
        for (const paragraph of paragraphs ?? []) {
            if (!paragraph || typeof paragraph !== 'object') continue;
            const repeated = enabled(paragraph.repeatable);
            const relation = String(paragraph.relation || (repeated ? paragraph.repeatable_table : '') || '').split('.').filter(Boolean);
            const path = [...prefix, ...relation];
            if (repeated) {
                if (prefix.length && relation.length) paths.push({ source: path, target: relation });
                continue;
            }
            visit(paragraph.form_paragraphs, path);
        }
    };
    visit(sheet?.form_paragraphs);
    return paths;
};

const cloneValue = (value) => {
    if (Array.isArray(value)) return value.map(cloneValue);
    if (isObject(value)) return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, cloneValue(child)]));
    return value;
};

export const normalizeNumberValue = (value) => {
    if (typeof value !== 'string' || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim())) return value;
    const number = Number(value);
    // Keep invalid input and unsafe integers available to validation without coercion/data loss.
    return Number.isFinite(number) && (!Number.isInteger(number) || Number.isSafeInteger(number)) ? number : value;
};

export const normalizeCheckboxValue = (value) => {
    if (typeof value === 'string') {
        const text = value.trim().toLowerCase();
        if (['1', 'true', 'on', 'yes'].includes(text)) return 1;
        if (['', '0', 'false', 'off', 'no'].includes(text)) return 0;
        return value;
    }
    if (value === true || value === 1) return 1;
    if (value === false || value === 0 || value == null) return 0;
    return value;
};

export const normalizeSelectionValue = (value, field) => {
    const option = (item) => {
        if (!isObject(item)) return item;
        const id = item[field.name] ?? item.id ?? item.value;
        return id === null || ['string', 'number', 'boolean'].includes(typeof id) ? id : item;
    };
    if (multipleSelectionTypes.has(field.form_field_type_id)) {
        if (!Array.isArray(value)) return value;
        return [...new Set(value.map(option).filter((item) => item != null)
            .map((item) => ['string', 'number'].includes(typeof item) ? String(item) : item))];
    }
    if (Array.isArray(value) && value.length === 0) return null;
    return option(Array.isArray(value) && value.length === 1 ? value[0] : value);
};

export const normalizeRepeatableItem = (item, field) => {
    if (item == null) return '';
    if (!isObject(item)) return item;
    for (const key of ['name', field.name, 'value']) {
        if (Object.prototype.hasOwnProperty.call(item, key)) return item[key] ?? '';
    }
    const entry = Object.entries(item).find(([key, value]) => key !== 'id' && !key.endsWith('_id')
        && !['created_at', 'updated_at', 'deleted_at'].includes(key) && typeof value === 'string');
    return entry ? entry[1] : item;
};

const normalizeFieldValue = (value, field) => {
    const normalizeScalar = (item) => {
        if (['number', 'slider'].includes(field.form_field_type_id)) return normalizeNumberValue(item);
        if (['checkbox', 'toggle'].includes(field.form_field_type_id)) return normalizeCheckboxValue(item);
        if (selectionTypes.has(field.form_field_type_id) || multipleSelectionTypes.has(field.form_field_type_id)) {
            return normalizeSelectionValue(item, field);
        }
        return item;
    };
    if (enabled(field.repeatable) && Array.isArray(value)) {
        return value.map((item) => normalizeScalar(field.repeatable_support_table ? normalizeRepeatableItem(item, field) : item));
    }
    return normalizeScalar(value);
};

/** Hydrate before flattening: field objects (JSON, files, selections) are atomic. */
export const initializeFormData = (record, sheet) => {
    const entries = fieldPaths(sheet);
    const repeatedPaths = rootRepeatablePaths(sheet);
    const samePath = (left, right) => left.length === right.length && left.every((part, index) => part === right[index]);
    // Draft proposals already contain form paths. Normalize those values by the
    // same metadata as the nested live DTO before choosing the proposed value.
    for (const { source, target } of repeatedPaths) {
        const matching = entries.filter(({ path }) => samePath(path.slice(0, source.length), source));
        entries.push(...matching.map(({ path, field }) => ({ path: [...target, ...path.slice(source.length)], field })));
    }
    const hydrate = (value, path = []) => {
        const field = matchingField(entries, path);
        if (field) return cloneValue(normalizeFieldValue(value, field));
        if (Array.isArray(value)) return value.map((item, index) => hydrate(item, [...path, String(index)]));
        if (!isObject(value)) return value;
        return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, hydrate(child, [...path, ...key.split('.')])]));
    };
    const result = {};
    const flatten = (value, path = []) => {
        if (path.length && (matchingField(entries, path) || !isObject(value) || !Object.keys(value).length)) {
            const repeatedPath = repeatedPaths.find(({ source }) => samePath(source, path));
            const target = (repeatedPath?.target ?? path).join('.');
            if (repeatedPath && Object.prototype.hasOwnProperty.call(record ?? {}, target)) return;
            result[target] = value;
            return;
        }
        if (!isObject(value)) return;
        for (const [key, child] of Object.entries(value)) flatten(child, [...path, ...key.split('.')]);
    };
    flatten(hydrate(record));
    return result;
};

/** Copy-on-write submission normalization; malformed JSON remains visible to validation. */
export const normalizeStructuredJsonPayload = (payload, sheet) => {
    const entries = fieldPaths(sheet);
    const normalizeValue = (value) => {
        if (typeof value !== 'string') return value;
        const normalized = normalizeJsonFieldModelValue(value);
        // Persistence decodes JSON strings. Keep string scalars encoded so
        // "hello", "true" and "123" cannot become invalid text or change type.
        return typeof normalized === 'string' ? value : normalized;
    };
    const visit = (value, path = []) => {
        const field = matchingField(entries, path);
        if (field) {
            if (!structuredTypes.has(field.form_field_type_id)) return value;
            if (enabled(field.repeatable) && Array.isArray(value)) {
                const items = value.map(normalizeValue);
                return items.some((item, index) => item !== value[index]) ? items : value;
            }
            return normalizeValue(value);
        }
        if (!Array.isArray(value) && !isObject(value)) return value;
        const items = Object.entries(value).map(([key, child]) => [key, visit(child, [...path, ...key.split('.')])]);
        if (items.every(([key, child]) => child === value[key])) return value;
        return Array.isArray(value) ? items.map(([, child]) => child) : Object.fromEntries(items);
    };
    return visit(payload);
};
